import uniqBy from 'lodash.uniqby'
import { filtersToQuery, useState, useSupabaseClient, useToast } from '#imports'
import type { Tables } from '~~/types/database.types.ts'
import type { Filters } from '~~/types/filters.d.ts'
import type { Spot } from '~~/types/spots.d.ts'
export const useExplore = () => {
  const spots = useState<Spot[]>('spots', () => [])
  const maps = useState<Tables<'maps'>[]>('maps', () => [])
  const categories = useState<Tables<'categories'>[]>('categories', () => [])
  const destinations = useState<Tables<'cities'>[]>('destinations', () => [])
  const destinationCategories = useState<Tables<'categories'>[]>(
    'destinationCategories',
    () => [],
  )

  const supabase = useSupabaseClient()
  const { showError } = useToast()

  const fetchSpots = async (
    filters?: Filters,
    search?: string,
    limit = 200,
  ) => {
    let query = supabase.from('spots').select(`
      *,
      map_slug:map_id!inner(
        current_slug
      ),
      country_name:cities_id!inner (
        country
      ),
      categories:category_id (
        id,
        name,
        icon
      ),
      cities:cities_id (
        id,
        name,
        country
      ),
      maps!inner (
        id,
        title,
        current_slug,
        is_published,
        profiles!inner(
          id,
          display_name,
          mappr_handle,
          profile_image_url
        )
      )
    `)

    query = filtersToQuery(
      {
        ...filters,
        search,
        searchReferences: 'spot.fts',
        limit,
      },
      query,
    )

    query.eq('maps.is_published', true)
    query.eq('is_published', true)
    query.order('visibility', { ascending: true })

    const { data, error } = await query
    if (error) showError('could not fetch spots')
    spots.value = data || []
  }

  const fetchCategories = async () => {
    const { data, error } = await supabase.from('categories').select('*')
    if (error) showError('could not fetch categories')
    categories.value = data || []
  }

  const fetchDestinations = async () => {
    const { data, error } = await supabase
      .from('cities')
      .select(`
        *,
        spots!inner (
          name,
          is_published,
          map_id,
          map:map_id!inner (
            *,
            user_id,
            profile:user_id (
              is_published
            )
          )
        )
      `)
      .eq('spots.map.is_published', true)
      .eq('spots.map.profile.is_published', true)

    if (error) showError('could not fetch destinations')
    const countries = uniqBy(data?.filter(Boolean), 'country').map(
      (country: Tables<'cities'>) => {
        return {
          label: country.country,
          value: country.country,
        }
      },
    )
    destinations.value = countries.sort((a, b) =>
      a.label.localeCompare(b.label),
    )
  }

  const fetchDestinationCategories = async () => {
    const categories: Tables<'categories'>[] = []
    spots.value.map(spot => categories.push(spot.categories))
    destinationCategories.value = uniqBy(categories.filter(Boolean), 'id')
  }

  const resetDestinationCategories = async () => {
    destinationCategories.value = []
  }

  const fetchMaps = async (filters?: Filters, search?: string, limit = 50) => {
    let query = supabase.from('maps').select(`
      *,
      spots!inner (
        *,
        map_slug:map_id!inner(
          current_slug
        ),
        categories:category_id (
          id,
          name,
          icon
        ),
        country_name:cities_id!inner (
          country
        ),
        cities:cities_id (
          id,
          name,
          country
        )
      ),
      profiles:user_id (
        id,
        mappr_handle,
        profile_image_url,
        display_name,
        stripe_account_id
      )
    `)

    query = filtersToQuery(
      {
        ...filters,
        search,
        searchReferences: 'maps.fts',
        limit,
      },
      query,
    )

    // query.eq('maps.is_published', true)
    query.eq('is_published', true)
    // query.order('visibility', { ascending: true })

    const { data, error } = await query
    if (error) showError('could not fetch maps')
    maps.value = data || []
  }

  return {
    fetchCategories,
    categories,
    fetchSpots,
    spots,
    fetchMaps,
    maps,
    fetchDestinations,
    destinations,
    fetchDestinationCategories,
    destinationCategories,
  }
}
